import { cn } from "@/lib/utils";

export interface TabItem {
  key: string;
  label: string;
  /** Something waiting behind this tab — shown as a small count when above zero. */
  count?: number;
}

interface TabBarProps {
  tabs: TabItem[];
  value: string;
  onChange: (key: string) => void;
  className?: string;
  /**
   * `underline` (default) is the quiet strip most detail pages use. `folder`
   * draws each tab as a bordered, raised tab that joins the content below —
   * for a page with enough tabs (a person's profile has up to nine) that
   * underlines run together and the active one is hard to find.
   */
  variant?: "underline" | "folder";
}

/**
 * Tab strip used on entity detail pages (Overview / … / Change history).
 *
 * Underline: the active tab gets a navy underline; the rest are muted.
 * Folder: every tab is a visible, bordered shape; the active one is raised, white
 * and capped with a navy bar, and the rest sit recessed on a tinted background.
 * A strip too wide for its container scrolls sideways rather than wrapping.
 */
export function TabBar({ tabs, value, onChange, className, variant = "underline" }: TabBarProps) {
  const folder = variant === "folder";
  return (
    <div
      role="tablist"
      className={cn(
        "flex overflow-x-auto border-b",
        folder ? "items-end gap-1" : "gap-0.5",
        className,
      )}
    >
      {tabs.map((tab) => {
        const active = value === tab.key;
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.key)}
            className={cn(
              "shrink-0 whitespace-nowrap text-sm font-semibold transition-colors",
              folder
                ? cn(
                    // -mb-px lets the active tab cover the strip's bottom border,
                    // so it reads as joined to the content beneath.
                    "-mb-px rounded-t-lg border border-b-0 px-3.5 py-2.5",
                    active
                      ? "border-t-2 border-t-primary bg-background text-foreground"
                      : "border-border bg-muted text-muted-foreground hover:bg-accent hover:text-foreground",
                  )
                : cn(
                    "-mb-px border-b-2 px-3.5 py-2.5",
                    active
                      ? "border-primary text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground",
                  ),
            )}
          >
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span className="ml-1.5 inline-flex min-w-5 items-center justify-center rounded-full bg-primary px-1.5 font-semibold text-2xs text-primary-foreground leading-5">
                {tab.count}
                <span className="sr-only"> new</span>
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
